<template>
  <div class="form-text-input">
    <label v-if="label" :for="name">{{ label }}<span>:</span></label>

    <input
      v-model.trim="value"
      :class="{ 'has-error': Boolean(errorLabel) }"
      :disabled="isDisabled"
      :required="isRequired"
      :name="name"
      :type="type"
      :autocomplete="autocomplete"
    />

    <template v-if="errorLabel">
      <small
        v-for="(error, index) in errorLabelList"
        :key="`error-label-${index}`"
        class="error-label"
      >
        {{ error }}
      </small>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

const emit = defineEmits(['update:modelValue'])

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },

  name: {
    type: String,
    default: null,
  },

  label: {
    type: String,
    default: null,
  },

  errorLabel: {
    type: [Array, String],
    default: null,
  },

  type: {
    type: String,
    default: 'text',
  },

  isDisabled: {
    type: Boolean,
    default: false,
  },

  isRequired: {
    type: Boolean,
    default: false,
  },

  autocomplete: {
    type: String,
    default: 'off',
  },
})

const value = computed<string>({
  get: (): string => props.modelValue as string,
  set: (val: string): void => emit('update:modelValue', val),
})

const errorLabelList = computed<Array<string>>(() =>
  Array.isArray(props.errorLabel) ? props.errorLabel : [props.errorLabel],
)
</script>

<style lang="scss" scoped>
@use 'sass:color';
@import 'src/assets/scss/controls';

$box-shadow: 0 0 (3 * $border-width);

.form-text-input {
  display: flex;
  flex-flow: column nowrap;
  gap: 0.5em;

  strong {
    margin: 0.125em 0;
  }

  input {
    @include control-style-base;

    background-color: $color-background;
    color: $color-text;
    box-shadow: $box-shadow $color-border;

    &:focus-visible {
      outline: $border-width solid $color-accent;
    }

    &:disabled {
      background-color: $color-background-disabled;
      color: $color-text-disabled;
      cursor: not-allowed;
      user-select: none;
    }

    &.has-error {
      background-color: color.adjust($color-danger, $lightness: 48%);
      color: $color-danger;
      box-shadow: $box-shadow $color-danger;

      &:focus-visible {
        outline-color: $color-danger;
      }
    }
  }

  .error-label {
    color: $color-danger;
  }
}
</style>
